import type { BreadcrumbConfig } from '@ingenico/react-section-utils';
import {
  capabilitiesForRouteAccess,
  getFirstAccessibleRouteBasedOnCapabilities,
  restrictRouteByEntityTypeUtil,
} from '@ingenico/react-section-utils';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import type { RouteObject } from 'react-router';
import { replace } from 'react-router';
import { firstValueFrom } from 'rxjs';
import { EntryComponent } from './EntryComponent';
import type { DeviceManagementSpiPort } from './domain/hexa/ports/spi.model';
import { applicationVersionPageLoader } from './pages/applications-version-details-page/applicationVersionPageLoader.util';
import { ResourcesPage } from './pages/resources-page/ResourcesPage.ctrl';
import { SyncConfigurationPage } from './pages/sync-configuration-page/SyncConfigurationPage.ctrl';

export function deviceManagementRoutes(spi: DeviceManagementSpiPort): RouteObject[] {
  return [
    {
      element: <EntryComponent />,
      children: [
        {
          index: true,
          loader: async () => {
            const defaultRoute = await firstValueFrom(spi.getNavigationRoutes()).then((navigationConfig) =>
              getFirstAccessibleRouteBasedOnCapabilities(navigationConfig, 'virtual-devices'),
            );

            return replace(defaultRoute);
          },
        },
        deviceManagementAppsRoutes(spi),
        deviceManagementResourcesRoutes(spi),
        deviceManagementSystemPackagesRoutes(spi),
        deviceManagementConfigurationsRoutes(spi),
        deviceManagementRkiRoutes(spi),
        deviceManagementOSRoutes(spi),
        deviceManagementConfigurationModulesRoutes(spi),
        deviceManagementDeploymentRoutes(spi),
        deviceManagementVirtualDevicesRoutes(spi),
        deviceManagementSyncConfigurationRoutes(spi),
      ],
    },
  ];
}

function deviceManagementResourcesRoutes(spi: DeviceManagementSpiPort): RouteObject {
  return {
    path: 'resources',
    handle: {
      breadcrumb: {
        labelKey: 'navigation.page.resources',
      } satisfies BreadcrumbConfig,
    },
    children: [
      {
        index: true,
        loader: capabilitiesForRouteAccess(spi, ['resources.list']),
        element: <ResourcesPage />,
      },
    ],
  };
}

function deviceManagementSystemPackagesRoutes(spi: DeviceManagementSpiPort): RouteObject {
  return {
    path: 'system-packages',
    handle: {
      breadcrumb: {
        labelKey: 'title.packageListPage',
        labelKeyNS: ALL_LNGS_NAMESPACES.SERVICE_CONFIGURATION,
      } satisfies BreadcrumbConfig,
    },
    children: [
      {
        index: true,
        loader: capabilitiesForRouteAccess(spi, ['systemPackages.list']),
        lazy: () =>
          import('./pages/system-packages-page/SystemPackagesPage.ctrl').then((m) => ({
            Component: m.SystemPackagesPage,
          })),
      },
      {
        path: 'create',
        loader: capabilitiesForRouteAccess(spi, ['systemPackages.create']),
        lazy: () =>
          import('./pages/create-package-page/CreatePackagePage.ctrl').then((m) => ({
            Component: m.CreatePackagePage,
          })),
        handle: {
          breadcrumb: {
            labelKey: 'section.systemPackage.title',
            labelKeyNS: ALL_LNGS_NAMESPACES.SERVICE_CONFIGURATION,
          } satisfies BreadcrumbConfig,
        },
      },
      {
        path: ':id',
        handle: {
          breadcrumb: {
            labelKey: 'navigation.page.details',
            useCurrentEntityPageLabelWhenSet: true,
            hasViewTab: true,
          } satisfies BreadcrumbConfig,
        },
        children: [
          {
            index: true,
            loader: capabilitiesForRouteAccess(spi, ['systemPackages.detail']),
            lazy: () =>
              import('./pages/system-package-details-page/SystemPackageDetailsPage.ctrl').then((m) => ({
                Component: m.SystemPackageDetailsPage,
              })),
          },
          {
            path: 'add-version',
            loader: capabilitiesForRouteAccess(spi, ['systemPackages.version.create']),
            lazy: () =>
              import('./pages/create-system-package-version-page/CreateSystemPackageVersionPage.ctrl').then((m) => ({
                Component: m.CreateSystemPackageVersionPage,
              })),
            handle: {
              breadcrumb: {
                labelKey: 'navigation.page.newVersion',
              } satisfies BreadcrumbConfig,
            },
          },
          {
            path: ':versionId',
            loader: capabilitiesForRouteAccess(spi, ['systemPackages.version.detail']),
            lazy: () =>
              import('./pages/system-package-version-details-page/SystemPackageVersionDetailsPage.ctrl').then((m) => ({
                Component: m.SystemPackageVersionDetailsPage,
              })),
            handle: {
              breadcrumb: {
                labelKey: 'navigation.page.version',
              } satisfies BreadcrumbConfig,
            },
          },
        ],
      },
    ],
  };
}

function deviceManagementAppsRoutes(spi: DeviceManagementSpiPort): RouteObject {
  return {
    path: 'apps',
    handle: {
      breadcrumb: {
        labelKey: 'navigation.page.apps',
      } satisfies BreadcrumbConfig,
    },
    children: [
      {
        index: true,
        loader: capabilitiesForRouteAccess(spi, ['apps.list']),
        lazy: () =>
          import('./pages/applications-page/ApplicationsPage.ctrl').then((m) => ({ Component: m.ApplicationsPage })),
      },
      {
        path: 'create',
        loader: capabilitiesForRouteAccess(spi, ['apps.create']),
        lazy: () =>
          import('./pages/create-application-page/CreateApplicationPage.ctrl').then((m) => ({
            Component: m.CreateApplicationPage,
          })),
        handle: {
          breadcrumb: {
            labelKey: 'title.createApp',
            labelKeyNS: ALL_LNGS_NAMESPACES.SERVICE_CONFIGURATION,
          } satisfies BreadcrumbConfig,
        },
      },
      {
        path: ':id',
        handle: {
          breadcrumb: {
            labelKey: 'navigation.page.details',
            useCurrentEntityPageLabelWhenSet: true,
            hasViewTab: true,
          } satisfies BreadcrumbConfig,
        },
        children: [
          {
            index: true,
            loader: capabilitiesForRouteAccess(spi, ['apps.detail']),
            lazy: () =>
              import('./pages/application-details-page').then((m) => ({ Component: m.DeviceApplicationDetailsPage })),
          },
          {
            path: 'add-version',
            loader: applicationVersionPageLoader(spi),
            lazy: () =>
              import('./pages/applications-version-details-page/ApplicationsVersionDetails.ctrl').then((m) => ({
                Component: m.CreateApplicationsVersionDetailsPage,
              })),
            handle: {
              breadcrumb: {
                labelKey: 'navigation.page.newVersion',
              } satisfies BreadcrumbConfig,
            },
          },
          {
            path: ':versionId',
            loader: capabilitiesForRouteAccess(spi, ['apps.version.detail']),
            lazy: () =>
              import('./pages/device-application-version-details-page/DeviceApplicationVersionDetailsPage.ctrl').then(
                (m) => ({ Component: m.DeviceApplicationVersionDetailsPage }),
              ),
            handle: {
              breadcrumb: {
                labelKey: 'navigation.page.version',
              } satisfies BreadcrumbConfig,
            },
          },
        ],
      },
    ],
  };
}

function deviceManagementOSRoutes(spi: DeviceManagementSpiPort): RouteObject {
  return {
    path: 'os',
    handle: {
      breadcrumb: {
        labelKey: 'title.osListPage',
        labelKeyNS: ALL_LNGS_NAMESPACES.DEVICE_APPS,
      } satisfies BreadcrumbConfig,
    },
    children: [
      {
        index: true,
        loader: capabilitiesForRouteAccess(spi, ['os.list']),
        lazy: () => import('./pages/os-page/OsPage.ctrl').then((m) => ({ Component: m.OsPage })),
      },
      {
        path: 'create',
        loader: capabilitiesForRouteAccess(spi, ['os.create']),
        lazy: () => import('./pages/create-os-page/CreateOsPage.ctrl').then((m) => ({ Component: m.CreateOsPage })),
        handle: {
          breadcrumb: {
            labelKey: 'section.os.title',
            labelKeyNS: ALL_LNGS_NAMESPACES.DEVICE_APPS,
          } satisfies BreadcrumbConfig,
        },
      },
      {
        path: ':id',
        handle: {
          breadcrumb: {
            labelKey: 'navigation.page.details',
            useCurrentEntityPageLabelWhenSet: true,
            hasViewTab: true,
          } satisfies BreadcrumbConfig,
        },
        children: [
          {
            index: true,
            loader: capabilitiesForRouteAccess(spi, ['os.detail']),
            lazy: () =>
              import('./pages/os-application-details-page/OSApplicationDetailsPage.ctrl').then((m) => ({
                Component: m.OSApplicationDetailsPage,
              })),
          },
          {
            path: 'add-version',
            loader: capabilitiesForRouteAccess(spi, ['os.version.create']),
            lazy: () =>
              import('./pages/create-os-version-page/CreateOsVersionPage.ctrl').then((m) => ({
                Component: m.CreateOsVersionPage,
              })),
            handle: {
              breadcrumb: {
                labelKey: 'navigation.page.newVersion',
              } satisfies BreadcrumbConfig,
            },
          },
          {
            path: 'version/:osVersionId',
            loader: capabilitiesForRouteAccess(spi, ['os.manageVersions']),
            lazy: () =>
              import('./pages/os-version-details/OsVersionDetails.ctrl').then((m) => ({
                Component: m.OsVersionDetails,
              })),
            handle: {
              breadcrumb: {
                labelKey: 'subtitle.version',
                labelKeyNS: ALL_LNGS_NAMESPACES.DEVICE_APPS,
              } satisfies BreadcrumbConfig,
            },
          },
        ],
      },
    ],
  };
}

function deviceManagementConfigurationsRoutes(spi: DeviceManagementSpiPort): RouteObject {
  return {
    path: 'configurations',
    loader: restrictRouteByEntityTypeUtil(spi, ['organization']),
    handle: {
      breadcrumb: {
        labelKey: 'title.configurationProfile',
        labelKeyNS: ALL_LNGS_NAMESPACES.CONFIGURATION_PROFILE,
      } satisfies BreadcrumbConfig,
    },
    children: [
      {
        index: true,
        loader: capabilitiesForRouteAccess(spi, ['configProfile.list']),
        lazy: () =>
          import('./pages/configuration-profile-list-page/ConfigurationProfileListPage.ctrl').then((m) => ({
            Component: m.ConfigurationProfileListPage,
          })),
      },
      {
        path: ':id',
        handle: {
          breadcrumb: {
            labelKey: 'navigation.page.details',
            useCurrentEntityPageLabelWhenSet: true,
          } satisfies BreadcrumbConfig,
        },
        children: [
          {
            index: true,
            loader: capabilitiesForRouteAccess(spi, ['configProfile.detail']),
            lazy: () =>
              import('./pages/configuration-profile-detail-page/ConfigurationProfileDetailPage.ctrl').then((m) => ({
                Component: m.ConfigurationProfileDetailPage,
              })),
          },
          {
            path: 'create',
            loader: capabilitiesForRouteAccess(spi, ['deployments.create']),
            lazy: () =>
              import('./pages/create-deployment-page/CreateDeploymentPageMain.ctrl').then((m) => ({
                Component: m.CreateDeploymentPageMain,
              })),
            handle: {
              breadcrumb: {
                labelKey: 'navigation.page.newDeployment',
              } satisfies BreadcrumbConfig,
            },
          },
        ],
      },
    ],
  };
}

function deviceManagementRkiRoutes(spi: DeviceManagementSpiPort): RouteObject {
  return {
    path: 'rki',
    handle: {
      breadcrumb: {
        labelKey: 'navigation.page.rki',
      } satisfies BreadcrumbConfig,
    },
    loader: capabilitiesForRouteAccess(spi, ['rki.detail']),
    lazy: () =>
      import('./pages/rki-service-details-page/RkiServiceDetailsPage.ctrl').then((m) => ({
        Component: m.RkiServiceDetailsPage,
      })),
    children: [
      {
        path: ':organizationId',
        children: [
          {
            index: true,
            loader: capabilitiesForRouteAccess(spi, ['rki.shouldEnableKeysetSegregation']),
            lazy: () =>
              import('./pages/rki-service-organization-details-page/RkiServiceOrganizationDetailsPage.ctrl').then(
                (m) => ({
                  Component: m.RkiServiceOrganizationDetailsPage,
                }),
              ),
          },
        ],
      },
    ],
  };
}

function deviceManagementConfigurationModulesRoutes(spi: DeviceManagementSpiPort): RouteObject {
  return {
    path: 'modules',
    loader: restrictRouteByEntityTypeUtil(spi, ['organization']),
    handle: {
      breadcrumb: {
        labelKey: 'navigation.page.modules',
        labelKeyNS: ALL_LNGS_NAMESPACES.DEVICE_APPS,
      } satisfies BreadcrumbConfig,
    },
    children: [
      {
        index: true,
        loader: capabilitiesForRouteAccess(spi, ['modules.list']),
        lazy: () => import('./pages/modules-page/ModulesPage.ctrl').then((m) => ({ Component: m.ModulesPage })),
      },
      {
        path: ':id',
        handle: {
          breadcrumb: {
            labelKey: 'navigation.page.details',
            useCurrentEntityPageLabelWhenSet: true,
            hasViewTab: true,
          } satisfies BreadcrumbConfig,
        },
        children: [
          {
            path: 'app',
            loader: capabilitiesForRouteAccess(spi, ['modules.detail']),
            lazy: () =>
              import('./pages/app-module-details/AppModuleDetails.ctrl').then((m) => ({
                Component: m.AppModuleDetails,
              })),
          },
          {
            path: 'system-package',
            loader: capabilitiesForRouteAccess(spi, ['modules.detail']),
            lazy: () =>
              import('./pages/system-package-module-details/SystemPackageModuleDetails.ctrl').then((m) => ({
                Component: m.SystemPackageModuleDetails,
              })),
          },
        ],
      },
    ],
  };
}

function deviceManagementDeploymentRoutes(spi: DeviceManagementSpiPort): RouteObject {
  return {
    path: 'deployments',
    loader: restrictRouteByEntityTypeUtil(spi, ['organization']),
    handle: {
      breadcrumb: {
        labelKey: 'title.deployments',
        labelKeyNS: ALL_LNGS_NAMESPACES.DEPLOYMENTS,
      } satisfies BreadcrumbConfig,
    },
    children: [
      {
        index: true,
        loader: capabilitiesForRouteAccess(spi, ['deployments.list']),
        lazy: () => import('./pages/deployments-page/Deployments.ctrl').then((m) => ({ Component: m.Deployments })),
      },
      {
        path: ':id',
        loader: capabilitiesForRouteAccess(spi, ['deployments.detail']),
        lazy: () =>
          import('./pages/deployment-details-page/DeploymentDetailsPage.ctrl').then((m) => ({
            Component: m.DeploymentDetailsPage,
          })),
        handle: {
          breadcrumb: {
            labelKey: 'navigation.page.details',
            useCurrentEntityPageLabelWhenSet: true,
          } satisfies BreadcrumbConfig,
        },
        children: [
          {
            path: ':targetDeviceId',
            loader: capabilitiesForRouteAccess(spi, ['deployments.detail']),
            lazy: () =>
              import('./pages/deployment-target-device-details-page/DeploymentTargetDeviceDetailsPage.ctrl').then(
                (m) => ({
                  Component: m.DeploymentTargetDeviceDetailsPage,
                }),
              ),
            handle: {
              breadcrumb: {
                labelKey: 'breadcrumb.deploymentTargetDeviceDetails',
                labelKeyNS: ALL_LNGS_NAMESPACES.DEPLOYMENTS,
              } satisfies BreadcrumbConfig,
            },
          },
        ],
      },
      {
        path: 'create',
        loader: capabilitiesForRouteAccess(spi, ['deployments.create']),
        lazy: () =>
          import('./pages/create-deployment-page/CreateDeploymentPageMain.ctrl').then((m) => ({
            Component: m.CreateDeploymentPageMain,
          })),
        handle: {
          breadcrumb: {
            labelKey: 'navigation.page.newDeployment',
          } satisfies BreadcrumbConfig,
        },
      },
      {
        path: 'uninstall',
        loader: capabilitiesForRouteAccess(spi, ['deployments.delete']),
        lazy: () =>
          import('./pages/deploy-deletion-request-page/DeployDeletionRequestPage.ctrl').then((m) => ({
            Component: m.DeployDeletionRequestPage,
          })),
        handle: {
          breadcrumb: {
            labelKey: 'navigation.page.deleteRequest',
          } satisfies BreadcrumbConfig,
        },
      },
    ],
  };
}

function deviceManagementLogicalDeviceLogCallsRoutes(spi: DeviceManagementSpiPort): RouteObject {
  return {
    path: 'calls/:callId',
    loader: restrictRouteByEntityTypeUtil(spi, ['organization']),
    children: [
      {
        index: true,
        handle: {
          breadcrumb: {
            labelKey: 'navigation.page.callLog',
          } satisfies BreadcrumbConfig,
        },
        lazy: () =>
          import('./pages/device-call-log-page/DeviceCallLogPage.ctrl').then((m) => ({
            Component: m.DeviceCallLogPage,
          })),
      },
    ],
  };
}

function deviceManagementVirtualDevicesRoutes(spi: DeviceManagementSpiPort): RouteObject {
  return {
    path: 'virtual-devices',
    loader: restrictRouteByEntityTypeUtil(spi, ['organization']),
    handle: {
      breadcrumb: {
        labelKey: 'navigation.page.virtualDevices',
      } satisfies BreadcrumbConfig,
    },
    children: [
      {
        index: true,
        loader: capabilitiesForRouteAccess(spi, ['logicalDevice.list']),
        lazy: () =>
          import('./pages/logical-devices-input-page/LogicalDevicesInputPage.ctrl').then((m) => ({
            Component: m.LogicalDevicesInputPage,
          })),
      },
      {
        path: ':logicalDeviceId',
        handle: {
          breadcrumb: {
            labelKey: 'navigation.page.details',
            useCurrentEntityPageLabelWhenSet: true,
            hasViewTab: true,
          } satisfies BreadcrumbConfig,
        },
        loader: capabilitiesForRouteAccess(spi, ['logicalDevice.detail']),
        lazy: () =>
          import('./pages/logical-devices-page/LogicalDevicesPage.ctrl').then((m) => ({
            Component: m.LogicalDeviceDetailsPage,
          })),
        children: [
          {
            path: ':deviceChangesId',
            loader: capabilitiesForRouteAccess(spi, ['logicalDevice.showDeviceChangesTab']),
            lazy: () =>
              import('./pages/provisioning-device-change-details-page/ProvisioningDeviceChangeDetailsPage.ctrl').then(
                (m) => ({ Component: m.ProvisioningDeviceChangeDetailsPage }),
              ),
            handle: {
              breadcrumb: {
                labelKey: 'deviceChangeDrawer.title',
                labelKeyNS: ALL_LNGS_NAMESPACES.TASKS,
              } satisfies BreadcrumbConfig,
            },
          },
        ],
      },
      {
        // Intentionally duplicate the path here,
        // Because <EditLogicalDevicePage /> should not be rendered as a child of <LogicalDeviceDetailsPage />
        path: ':logicalDeviceId',
        handle: {
          breadcrumb: {
            labelKey: 'navigation.page.details',
            useCurrentEntityPageLabelWhenSet: true,
            hasViewTab: true,
          } satisfies BreadcrumbConfig,
        },
        children: [
          {
            path: 'screen-share',
            loader: capabilitiesForRouteAccess(spi, ['logicalDevice.showScreenShare']),
            lazy: () =>
              import('./pages/logical-device-screen-share-page/DeviceManagementLogicalDeviceScreenSharePage.ctrl').then(
                (m) => ({ Component: m.DeviceManagementLogicalDeviceScreenSharePage }),
              ),
            handle: {
              breadcrumb: {
                labelKey: 'navigation.page.screenShare',
                useCurrentEntityPageLabelWhenSet: true,
                hasViewTab: true,
              } satisfies BreadcrumbConfig,
            },
          },
          deviceManagementLogicalDeviceLogCallsRoutes(spi),
          deviceManagementLogicalDeviceDiagnosticsRoutes(),
        ],
      },
    ],
  };
}

function deviceManagementLogicalDeviceDiagnosticsRoutes(): RouteObject {
  return {
    path: 'diagnostics',
    children: [
      {
        index: true,
        lazy: () =>
          import('./components/device-diagnostics-list-content/DeviceDiagnosticsListContent.ctrl').then((m) => ({
            Component: m.DeviceDiagnosticsListContent,
          })),
        handle: {
          breadcrumb: {
            labelKey: 'navigation.page.diagnosticsList',
          } satisfies BreadcrumbConfig,
        },
      },
      {
        path: ':id',
        children: [
          {
            handle: {
              breadcrumb: {
                labelKey: 'navigation.page.diagnostics',
              } satisfies BreadcrumbConfig,
            },
            index: true,
            lazy: () =>
              import('./pages/device-diagnostic-page/DeviceDiagnosticPage.ctrl').then((m) => ({
                Component: m.DeviceDiagnosticPage,
              })),
          },
        ],
      },
    ],
  };
}

function deviceManagementSyncConfigurationRoutes(spi: DeviceManagementSpiPort): RouteObject {
  return {
    path: 'sync-configuration',
    handle: {
      breadcrumb: {
        labelKey: 'navigation.page.syncConfiguration',
      } satisfies BreadcrumbConfig,
    },
    children: [
      {
        index: true,
        loader: capabilitiesForRouteAccess(spi, ['logicalDevice.msSyncConfiguration']),
        element: <SyncConfigurationPage />,
      },
    ],
  };
}
